import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { fetchIntake } from './api';
import { readIntakeStream } from './sse';
import type { IntakeState } from './types';

/**
 * Query key for one request's intake conversation.
 *
 * NOT yet in lib/queryKeys.ts — that file is out of this feature's ownership
 * (three other agents are working elsewhere in the tree) and intake is brand
 * new, so this is the only writer of the key today. Follows the file's HARD
 * RULE regardless: `['ws', workspaceId, 'intake', requestId]`, workspace id
 * first, so a tenant's cache can be blown away by prefix like every other key.
 * REPORTED for the lead to fold in.
 */
export const intakeQueryKeys = {
  intake: (workspaceId: string, requestId: string) => ['ws', workspaceId, 'intake', requestId] as const,
};

const POLL_INTERVAL_MS = 4000;
const MAX_STREAM_RETRIES = 5;

function isTerminal(status: IntakeState['status'] | undefined): boolean {
  return status === 'settled' || status === 'failed';
}

/** Aborts early if the caller's own signal fires while waiting. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Live intake state for one request.
 *
 * Three channels answer the same question, in decreasing order of freshness:
 * the SSE stream (near-instant), a 4s interval poll of the GET (the belt to
 * the stream's braces — also the whole thing if the stream can never connect,
 * e.g. because `/intake/stream` has not shipped yet), and the GET itself,
 * fired fresh on every mount so a client who navigates away and back sees
 * where things actually stand rather than stale component state.
 *
 * Returning the plain `useQuery` result (not a repackaged subset) is what
 * makes "current state, not just component state" true for free — the cache
 * from a previous mount renders immediately while a fresh GET is in flight.
 */
export function useIntakeState(workspaceId: string, requestId: string): UseQueryResult<IntakeState> {
  const queryKey = intakeQueryKeys.intake(workspaceId, requestId);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const next = await fetchIntake(workspaceId, requestId, signal);
      // The plain GET never carries phase/phases/phaseStep — intake.service.ts's
      // watch() synthesizes those only for the SSE stream and never persists
      // them (see its saveSnapshot comment). Without this merge, this 4s poll
      // landing between two SSE frames would blank the checklist back to its
      // single-line fallback and then the next frame would restore it a second
      // later — a visible flicker that reads as the panel resetting, not the
      // step it actually is standing still.
      const previous = queryClient.getQueryData<IntakeState>(queryKey);
      if (next.phase === undefined && previous?.phase !== undefined) {
        return { ...next, phase: previous.phase, phases: previous.phases, phaseStep: previous.phaseStep };
      }
      return next;
    },
    refetchInterval: (q) => (isTerminal(q.state.data?.status) ? false : POLL_INTERVAL_MS),
  });

  const settled = isTerminal(query.data?.status);
  // Only once the GET has answered. Opening the stream on mount, before
  // anyone knows whether there is anything to watch, spent one of the
  // model-route rate limit's twenty-a-minute on every thread opened — and a
  // thread list is opened a lot. Nearly every request's intake is long over.
  const known = query.data !== undefined;

  useEffect(() => {
    // Nothing left to watch once a round has concluded — reconnecting to a
    // stream for a settled or failed request would just spin forever.
    if (!known || settled) return;
    return acquireStream(queryClient, workspaceId, requestId);
    // `queryKey` is deliberately NOT a dependency. It is a fresh array every
    // render, and every SSE frame is a render — so depending on it tore the
    // stream down and reopened it on every frame, which is a request against
    // the model-route rate limit per frame.
  }, [workspaceId, requestId, queryClient, settled, known]);

  return query;
}

/**
 * One stream per request per tab, however many components are watching it.
 *
 * A thread being filed has two readers of the same state — @rundown's
 * "working on it" line and the question card — and each used to open its own
 * stream. Reference-counted: the first reader opens it, the last one to
 * unmount closes it.
 */
const liveStreams = new Map<string, { users: number; stop: () => void }>();

function acquireStream(queryClient: QueryClient, workspaceId: string, requestId: string): () => void {
  const key = `${workspaceId}/${requestId}`;
  const existing = liveStreams.get(key);
  if (existing !== undefined) {
    existing.users += 1;
  } else {
    liveStreams.set(key, { users: 1, stop: runStream(queryClient, workspaceId, requestId) });
  }
  return () => {
    const entry = liveStreams.get(key);
    if (entry === undefined) return;
    entry.users -= 1;
    if (entry.users > 0) return;
    liveStreams.delete(key);
    entry.stop();
  };
}

/** The reconnecting read loop. Returns how to stop it. */
function runStream(queryClient: QueryClient, workspaceId: string, requestId: string): () => void {
  const queryKey = intakeQueryKeys.intake(workspaceId, requestId);
  const controller = new AbortController();
  let cancelled = false;

  void (async () => {
    let retries = 0;
    // `cancelled` is checked after every await below instead of in this
    // condition — it can only flip true while an await is suspended (unmount,
    // or a terminal frame arriving), never between one loop turn and the next.
    while (retries <= MAX_STREAM_RETRIES) {
      try {
        // eslint-disable-next-line no-await-in-loop -- a reconnect only happens after the previous stream ends
        await readIntakeStream(
          workspaceId,
          requestId,
          (next) => {
            queryClient.setQueryData<IntakeState>(queryKey, next);
            retries = 0;
            if (isTerminal(next.status)) cancelled = true;
          },
          controller.signal,
        );
        if (cancelled) return;
        // The stream closed without ever sending a terminal frame — the
        // server ended the connection early. Fall through and reconnect;
        // the interval poll keeps the screen honest in the gap.
      } catch {
        // Network error, 404 (the stream endpoint not deployed yet), a 429,
        // or an aborted read on unmount — all land here and all get the same
        // treatment: back off and try again rather than give up outright.
      }
      if (cancelled) return;
      retries += 1;
      // eslint-disable-next-line no-await-in-loop -- the backoff is the delay
      await sleep(Math.min(1000 * 2 ** retries, 15_000), controller.signal);
    }
    // Retries exhausted: stop trying to stream. The interval poll is still
    // running — that is the feature working in degraded mode, not a dead
    // screen.
  })();

  return () => {
    cancelled = true;
    controller.abort();
  };
}
